import { schema } from '@/components/Patient/forms/AddressForm/AddressForm.schema';
import { FF_ENABLE_GOOGLE_PLACES_AUTOCOMPLETE } from '@/constants/featureFlags';
import settings from '@/data/constants';
import { useFeatureFlags } from '@/hooks';
import { useGetAustralianStates } from '@/hooks/patients/useGetAustralianStates';
import useAddressValidation from '@/hooks/useAddressValidation';
import { useAppStore } from '@/state-management';
import { zodResolver } from '@hookform/resolvers/zod';
import type { AddressDataType } from '@montugroup/design-system';
import { LocationInput } from '@montugroup/design-system';
import type { PlaceDetails } from '@montugroup/design-system/LocationInput';
import type { UpdatePatientBasicInfoDto } from '@montugroup/pms-api-contracts';
import { Box, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { FieldValues } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import { AutocompleteElement, FormContainer, TextFieldElement } from 'react-hook-form-mui';
import type { z } from 'zod';
import FormSection from '../../FormSection';
import { PATIENT_DETAILS_FORM_NAMES } from '../../Patient.constants';

export type AddressFormFields = z.infer<typeof schema>;

type UpdatePatientBasicInfoDtoAttributes = UpdatePatientBasicInfoDto['attributes'];

export type AddressFormProps = {
  editable?: boolean;
  defaultValues?: AddressFormFields;
  onSubmit?: (data: AddressFormFields) => void;
  isLoading?: boolean;
};

type StyledLocationInputContainerProps = {
  editable?: boolean;
  hasError: boolean;
};

const StyledLocationInput = styled(LocationInput)({
  input: {
    height: '35px',
  },
  label: {
    marginTop: '0.5rem',
  },
});

const StyledLocationInputContainer = styled(Box, {
  shouldForwardProp: (props) => props !== 'hasError' && props !== 'editable',
})<StyledLocationInputContainerProps>(({ theme, editable, hasError }) => ({
  pointerEvents: editable ? 'auto' : 'none',
  opacity: editable ? 1 : 0.6,
  borderRadius: '4px',
  '.MuiInputBase-root': {
    border: hasError ? `1px solid ${theme.palette.error.main}` : 'none',
  },
  '.MuiInputLabel-root': {
    pointerEvents: editable ? 'auto' : 'none',
  },
}));

export const AddressForm = (props: AddressFormProps) => {
  const { defaultValues, editable, onSubmit, isLoading } = props;
  const { flags } = useFeatureFlags();

  const FORM_NAME = PATIENT_DETAILS_FORM_NAMES.PATIENT_BASIC_INFO;

  const isGooglePlacesAutocompleteEnabled = flags[FF_ENABLE_GOOGLE_PLACES_AUTOCOMPLETE];

  const { data: listOfAustralianStates } = useGetAustralianStates();
  const { validateStreetNumber } = useAddressValidation();

  const setPatientIhiVerification = useAppStore.use.setPatientIhiVerification();

  const formattedAustralianStates = listOfAustralianStates?.map((state) => {
    return { id: state.id, label: state.name };
  });

  const formContext = useForm<FieldValues>({
    defaultValues,
    values: defaultValues,
    shouldFocusError: false, // need to set to 'false' otherwise it will focus the input until error is resolved
    resolver: zodResolver(schema),
  });

  const { getValues } = formContext;
  const formValues = getValues();

  const transform = (dirtyValues: FieldValues): UpdatePatientBasicInfoDtoAttributes => {
    // Merge streetLine1 and streetLine2 to a single string separated by $$$$
    // However, when only one of them is being updated, we check the other one if it is populated
    // If it is, we make sure to append the value to the address.
    // We do this so we don't overwrite streetLine1 / streetLine2 with empty strings when only one of
    // them is being updated.

    return {
      residentialAddress: {
        address:
          [dirtyValues?.streetLine1 ?? formValues?.streetLine1, dirtyValues?.streetLine2 ?? formValues?.streetLine2]
            .filter(Boolean)
            .join('$$$$') || undefined,
        city: dirtyValues?.city ?? undefined,
        zipCode: dirtyValues?.zipCode ?? undefined,
        stateId: dirtyValues?.state?.id ?? dirtyValues?.state ?? undefined,
      },
    } as UpdatePatientBasicInfoDtoAttributes;
  };

  const isEffectivelyEditable = editable && typeof onSubmit === 'function';

  /*
    formContext.formState.isDirty was not reliably being set to `true` when the autocomplete data was being
    pre-filled. This was causing issues with the values not being submitted in the payload. This function
    compares the default form values and the current values. It is essentially a custom `isDirty` check.
 */
  const hasAddressChanged = (addressFormData: AddressFormFields): boolean => {
    return JSON.stringify(defaultValues) !== JSON.stringify(addressFormData);
  };

  const { setValue, setError, clearErrors, control, formState } = formContext;
  const handleOnSelectedStreetAddress = (addressData: AddressDataType) => {
    const placeDetails: PlaceDetails = {
      address_components: [
        {
          long_name: addressData.street_number,
          short_name: addressData.street_number,
          types: ['street_number'],
        },
      ],
    };

    const validationResult = validateStreetNumber(placeDetails);
    if (!validationResult.isValid) {
      setError('streetLine1', {
        message: `${validationResult.errorMessageTitle} ${validationResult.errorMessage}`,
      });
      return;
    }

    clearErrors('streetLine1');

    const stateId = listOfAustralianStates?.find((state) => state.name === addressData.state)?.id;

    setValue('streetLine1', `${addressData.subpremise} ${addressData.street_number} ${addressData.address}`.trim());
    setValue('city', addressData.suburb.trim());
    setValue('zipCode', addressData.postcode.trim());
    setValue('state', stateId as number);
  };

  const handleOnResetStreetAddress = () => {
    clearErrors('streetLine1');
    setValue('streetLine1', null);
    setValue('city', null);
    setValue('zipCode', null);
    setValue('state', null);
  };

  const handleOnSubmit = (formData: FieldValues) => {
    const payload = {
      streetLine1: formData.streetLine1,
      streetLine2: formData.streetLine2,
      city: formData.city,
      zipCode: formData.zipCode,
      state: formData.state.id || formData.state,
    };

    const isDirty = hasAddressChanged(payload);

    if (onSubmit && isDirty) {
      onSubmit(payload);
    }
  };

  const handleOnSuccess = () => {
    setPatientIhiVerification(true);
  };

  return (
    <FormSection
      title="Residential Address"
      formName={FORM_NAME}
      formContext={formContext}
      onSubmit={handleOnSubmit}
      isLoading={isLoading}
      transform={transform}
      onSuccess={handleOnSuccess}
    >
      <FormContainer formContext={formContext}>
        <Grid container columnSpacing={10} rowSpacing={5} marginY={2} data-testid="address-form">
          {isGooglePlacesAutocompleteEnabled ? (
            <Grid item xs={12} data-testid="street-address-google-input">
              <Controller
                name="streetLine1"
                control={control}
                render={({ field, fieldState }) => (
                  <StyledLocationInputContainer editable={isEffectivelyEditable} hasError={!!fieldState.error}>
                    <StyledLocationInput
                      id={'streetLine1'}
                      googlePlacesApiKey={settings.googlePlacesApiKey as string}
                      label="Street Address *"
                      onSelectedAddress={handleOnSelectedStreetAddress}
                      onReset={handleOnResetStreetAddress}
                      error={fieldState.error?.message}
                      disabled={!isEffectivelyEditable}
                      {...field}
                    />
                  </StyledLocationInputContainer>
                )}
              />
            </Grid>
          ) : (
            <Grid item xs={12} data-testid="street-address-textfield-input">
              <TextFieldElement
                name="streetLine1"
                label="Street Address *"
                fullWidth
                disabled={!isEffectivelyEditable}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={12} lg={6}>
            <TextFieldElement name="streetLine2" label="Address Line 2" fullWidth disabled={!isEffectivelyEditable} />
          </Grid>
          <Grid item xs={12} lg={6}>
            <TextFieldElement name="city" label="Suburb *" fullWidth disabled={!isEffectivelyEditable} />
          </Grid>
          <Grid item xs={12} lg={6}>
            <TextFieldElement
              name="zipCode"
              label="Postcode *"
              fullWidth
              disabled={!isEffectivelyEditable}
              error={!!formState?.errors?.zipCode}
              helperText={formState?.errors?.zipCode?.message?.toString() ?? ''}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <AutocompleteElement
              name="state"
              label="State *"
              options={formattedAustralianStates ?? []}
              autocompleteProps={{
                disabled: !isEffectivelyEditable,
              }}
            />
          </Grid>
        </Grid>
      </FormContainer>
    </FormSection>
  );
};
